import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import type { SlideManifestEntry } from "@-/dev.improve.haniverse.assets.slides";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useCompletedSlideKeysQueryOptions } from "@-/dev.improve.haniverse.lib.slide.read";
import type { LevelCardSlides } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";

const SLIDE_PAGE_ICON_SIZE = 11;

/**
 * A level tile's accompanying slides as small tilted pages, laid over the tile by the caller
 * (`className` places the row): at most one page on the left for slides that lead into the card,
 * and at most one on the right for slides that follow it — never stacked icons on the same side,
 * even when that side has several slides. A side's page carries a check only once every slide on
 * that side is completed. Taps pass through to the tile underneath.
 *
 * The card plus these slides is one **lesson** (see `lib/lesson/TERMINOLOGY.md`).
 */
export function LevelSlidePages({
  className,
  slides,
}: {
  className?: string;
  slides: LevelCardSlides;
}) {
  const { data: completedSlideKeys } = useQuery(useCompletedSlideKeysQueryOptions());
  const isCompleted = (slide: SlideManifestEntry) => completedSlideKeys?.has(slide.key) === true;
  const allSlides = [...slides.before, ...slides.after];
  const completedCount = allSlides.filter((slide) => isCompleted(slide)).length;
  return (
    <View
      accessibilityLabel={`Slides: ${completedCount} of ${allSlides.length} completed`}
      accessible
      className={cn("flex-row items-center", className)}
      pointerEvents="none"
    >
      <SlidePagesColumn isCompleted={isCompleted} side="before" slides={slides.before} />
      <SlidePagesColumn isCompleted={isCompleted} side="after" slides={slides.after} />
    </View>
  );
}

function SlidePagesColumn({
  isCompleted,
  side,
  slides,
}: {
  isCompleted: (slide: SlideManifestEntry) => boolean;
  side: keyof LevelCardSlides;
  slides: readonly SlideManifestEntry[];
}) {
  // Always mount the column (even empty) so the parent's `justify-between` still pins the other
  // side to the opposite corner.
  const representative = slides[0];
  const completed = representative !== undefined && slides.every((slide) => isCompleted(slide));
  return (
    <View className={side === "before" ? "-rotate-12" : "rotate-12"}>
      {representative === undefined ? null : (
        <Icon
          className={completed ? "text-muted-foreground" : "text-muted-foreground/50"}
          name={completed ? "TbFileCheck" : "TbFile"}
          size={SLIDE_PAGE_ICON_SIZE}
          testID={`level-slide-page-${representative.key}`}
        />
      )}
    </View>
  );
}
